import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import ApiIcon from "@mui/icons-material/Api";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate, Link } from "react-router-dom";
import useSimApi from "src/hooks/useSimApi";
import useAuth from "src/context/AuthContext/useAuth";

const SIMULATOR_TYPE = process.env.REACT_APP_SIMULATOR_TYPE || "target";

const OAUTH_MODELS = ["OAUTH2_CLIENT_CREDENTIALS", "OAUTH2_AUTHORIZATION_CODE", "BEARER_TOKEN"];

const AUTH_MODEL_LABELS: Record<string, string> = {
  OAUTH2_CLIENT_CREDENTIALS: "OAuth 2.0 — Client Credentials",
  OAUTH2_AUTHORIZATION_CODE: "OAuth 2.0 — Authorization Code",
  BEARER_TOKEN: "Bearer Token",
  BASIC: "Basic Auth",
  BASIC_WITH_SESSION_TOKEN: "Basic Auth + Session Token",
  QUERY_STRING: "Query String (API key)",
};

const AUTH_MODEL_SHORT: Record<string, string> = {
  OAUTH2_CLIENT_CREDENTIALS: "Client Credentials",
  OAUTH2_AUTHORIZATION_CODE: "Auth Code",
  BEARER_TOKEN: "Bearer Token",
  BASIC: "Basic Auth",
  BASIC_WITH_SESSION_TOKEN: "Basic + Session",
  QUERY_STRING: "Query String",
};

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton size="small" onClick={copy} sx={{ ml: 0.5, color: "#6b7280" }}>
        {copied ? <CheckIcon sx={{ fontSize: 14, color: "#22c55e" }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
      </IconButton>
    </Tooltip>
  );
};

const linkStyle: React.CSSProperties = {
  flex: 1,
  fontFamily: "monospace",
  fontSize: 13,
  color: "#7166cf",
  wordBreak: "break-all",
  textDecoration: "none",
};

const UrlRow: React.FC<{ label: string; url: string; internal?: string }> = ({ label, url, internal }) => (
  <Box sx={{ display: "flex", alignItems: "center", py: "10px", borderBottom: "1px solid #f3f4f6", "&:last-child": { borderBottom: "none" } }}>
    <Typography sx={{ minWidth: 180, fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
      {label}
    </Typography>
    {internal ? (
      <Link to={internal} style={linkStyle}>{url}</Link>
    ) : (
      <a href={url} target="_blank" rel="noopener noreferrer" style={linkStyle}>{url}</a>
    )}
    <CopyButton text={url} />
  </Box>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", py: "10px", borderBottom: "1px solid #f3f4f6", "&:last-child": { borderBottom: "none" } }}>
    <Typography sx={{ minWidth: 180, fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: "monospace", fontSize: 13, flex: 1, wordBreak: "break-all" }}>
      {value}
    </Typography>
    <CopyButton text={value} />
  </Box>
);

const Dashboard: React.FC = () => {
  const { request } = useSimApi();
  const { connectorId, selectedConnectorId } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState<any>(null);
  const [endpointCount, setEndpointCount] = useState<number | null>(null);
  const [logCount, setLogCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeConnectorId = selectedConnectorId ?? connectorId;
  // Prefer the deploy-time public base (set on the deployed simulator-api)
  // so the URLs we display stay canonical regardless of where the admin
  // happens to be viewing the UI from. Falls back to window.location.origin
  // in local dev where the env var isn't set.
  const origin: string = config?.public_base_url || window.location.origin;
  const mockApiBase = activeConnectorId ? `${origin}/c/${activeConnectorId}` : null;

  useEffect(() => {
    Promise.all([
      request("/api/v1/config"),
      request("/api/v1/endpoints"),
      request("/api/v1/logs"),
    ])
      .then(async ([cfgRes, epRes, logRes]) => {
        if (cfgRes.ok) setConfig(await cfgRes.json());
        if (epRes.ok) setEndpointCount((await epRes.json()).filter((ep: any) => !ep.builtin).length);
        if (logRes.ok) setLogCount((await logRes.json()).length);
      })
      .catch(() => setError("Could not load data."));
  }, []);

  const authModel: string = config?.auth_model ?? "OAUTH2_CLIENT_CREDENTIALS";
  const isOAuth = OAUTH_MODELS.includes(authModel);
  const isBasic = authModel === "BASIC" || authModel === "BASIC_WITH_SESSION_TOKEN";
  const isQueryString = authModel === "QUERY_STRING";

  const urls = mockApiBase ? [
    { label: "Documentation", url: `${mockApiBase}/documentation` },
    { label: "Swagger UI", url: `${mockApiBase}/docs` },
    { label: "OpenAPI spec (JSON)", url: `${mockApiBase}/openapi.json` },
    { label: "API base URL", url: mockApiBase },
  ] : [
    { label: "Documentation", url: `${origin}/documentation`, internal: "/documentation" },
    { label: "Swagger UI", url: `${origin}/docs` },
  ];

  return (
    <Box sx={{ p: 4, maxWidth: 900 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={4}>
        <Typography variant="h5" fontWeight={600}>
          {config?.name || "Simulator Home"}
        </Typography>
        <Chip
          label={SIMULATOR_TYPE.toUpperCase()}
          size="small"
          color={SIMULATOR_TYPE === "target" ? "primary" : "secondary"}
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Stats */}
      <Stack direction="row" spacing={2} mb={4} flexWrap="wrap">
        <StatCard
          title="Configured endpoints"
          value={endpointCount ?? "—"}
          action={() => navigate("/endpoints")}
          actionLabel="Manage"
          icon={<ApiIcon />}
          color="#7166cf"
        />
        <StatCard
          title="Logged requests"
          value={logCount ?? "—"}
          action={() => navigate("/logs")}
          actionLabel="View logs"
          icon={<ListAltIcon />}
          color="#0891b2"
        />
        <StatCard
          title="Auth model"
          value={config ? (AUTH_MODEL_SHORT[authModel] ?? authModel) : "—"}
          action={() => navigate("/settings")}
          actionLabel="Settings"
          icon={<SettingsIcon />}
          color="#059669"
        />
      </Stack>

      {/* Simulator URLs */}
      {urls.length > 0 && (
        <Box mb={4}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Simulator URLs
          </Typography>
          <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1, px: 2 }}>
            {urls.map((u) => (
              <UrlRow key={u.label} label={u.label} url={u.url} internal={(u as any).internal} />
            ))}
          </Box>
        </Box>
      )}

      {/* Authentication section */}
      {config && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              Authentication towards simulator
            </Typography>
            <Chip
              label={AUTH_MODEL_LABELS[authModel] ?? authModel}
              size="small"
              sx={{ fontSize: 11, height: 20, bgcolor: "#f3f4f6", color: "#374151" }}
            />
          </Stack>
          <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1, px: 2 }}>
            {/* Credentials */}
            <InfoRow
              label={isBasic ? "username" : isQueryString ? "api_key" : "client_id"}
              value={config.client_id}
            />
            <InfoRow
              label={isBasic ? "password" : isQueryString ? "api_secret" : "client_secret"}
              value={config.client_secret}
            />

            {/* OAuth-specific */}
            {isOAuth && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", py: "10px", borderBottom: "1px solid #f3f4f6" }}>
                  <Typography sx={{ minWidth: 180, fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
                    token_ttl
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>{config.token_ttl}s</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", py: "10px", borderBottom: "1px solid #f3f4f6" }}>
                  <Typography sx={{ minWidth: 180, fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
                    scopes
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {config.scopes?.join(", ") || "any (unrestricted)"}
                  </Typography>
                </Box>
                <UrlRow label="token endpoint" url={`${mockApiBase ?? origin}/oauth/token`} />
                {authModel === "OAUTH2_AUTHORIZATION_CODE" && (
                  <UrlRow label="authorize endpoint" url={`${mockApiBase ?? origin}/oauth/authorize`} />
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Authentication towards engine — credentials the simulator presents
          when calling back into the Strawbay engine. Sourced from
          Company → Flow Access Settings in the Console. */}
      {config && (
        <Box mt={4}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Authentication towards engine
          </Typography>
          <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1, px: 2 }}>
            {config.strawbay_client_id || config.strawbay_client_secret ? (
              <>
                <InfoRow label="client_id" value={config.strawbay_client_id || ""} />
                <InfoRow label="client_secret" value={config.strawbay_client_secret || ""} />
              </>
            ) : (
              <Box sx={{ py: "14px", display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: "#6b7280", flex: 1 }}>
                  Not configured — paste the pair from Company → Flow Access
                  Settings into Settings.
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate("/settings")}
                  sx={{ color: "#7166cf", textTransform: "none" }}
                >
                  Open Settings →
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  action: () => void;
  actionLabel: string;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, action, actionLabel, icon, color }) => (
  <Card sx={{ minWidth: 180, flex: "0 0 auto" }}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
      </Stack>
      <Typography variant="h4" fontWeight={700} mb={1}>
        {value}
      </Typography>
      <Button size="small" onClick={action} sx={{ color, textTransform: "none", p: 0 }}>
        {actionLabel} →
      </Button>
    </CardContent>
  </Card>
);

export default Dashboard;
